<template>
  <div class="user-status-filter-item place">
    <label class="item-label" v-t.preserve="'appUserStatusFilterPlace.place'" />
    <div class="action-container">
      <place-selector
        ref="place-selector"
        v-bind:id-prefix="'user-status-filter-place'"
        v-bind:select-type="0"
        v-bind:selected-all-place-label="
          $t('appUserStatusFilterPlace.allPlaces')
        "
        v-bind:keyword-target="$store.getters['place/keywordTargets']"
        v-bind:indoor-level-id="indoorLevelId"
        v-bind:on-api-error="handleApiError"
        v-on:place-keyword-target-selectend="
          $store.dispatch('place/setKeywordTargets', $event)
        "
        v-on:place-selectend="$_onPlaceSelected"
      />
      <div v-if="localValue.placeId" class="criteria-container">
        <!-- 1行目 -->
        <label
          class="text"
          v-t.preserve="'appUserStatusFilterPlace.atThisPlace'"
        />
        <div class="radio-container">
          <label class="label">
            <b-form-radio
              v-bind:value="PLACE_RELATIONSHIP.IS"
              v-model="localValue.relationship"
            />
            <span class="text" v-t.preserve="'appUserStatusFilterPlace.is'" />
          </label>
          <label class="label">
            <b-form-radio
              v-bind:value="PLACE_RELATIONSHIP.CHECKIN"
              v-model="localValue.relationship"
            />
            <span
              class="text"
              v-t.preserve="'appUserStatusFilterPlace.checkingIn'"
            />
          </label>
        </div>
        <!-- 2行目 -->
        <label
          class="text"
          v-t.preserve="'appUserStatusFilterPlace.fromThisPlace'"
        />
        <div class="radio-container">
          <label class="label">
            <b-form-radio
              v-bind:value="PLACE_RELATIONSHIP.DISTANCE"
              v-model="localValue.relationship"
            />
            <b-form-select
              size="sm"
              v-bind:disabled="
                localValue.relationship !== PLACE_RELATIONSHIP.DISTANCE
              "
              v-bind:options="DISTANCE_OPTIONS"
              v-model="localValue.distance"
              class="distance-selector"
            />
            <span
              class="text"
              v-t.preserve="'appUserStatusFilterPlace.orLess'"
            />
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useHandleApiError } from '@/composables/UseHandleApiError'
import { PLACE_RELATIONSHIP } from '@/composables/member/UseUserStatusFilterCondition'

/**
 * 距離の選択肢
 */
const DISTANCE_OPTIONS = [
  { value: 5, text: '5m' },
  { value: 10, text: '10m' },
  { value: 50, text: '50m' },
  { value: 100, text: '100m' },
  { value: 500, text: '500m' },
  { value: 1000, text: '1km' },
  { value: 5000, text: '5km' },
  { value: 10000, text: '10km' },
  { value: 100000, text: '100km' },
]

export default {
  name: 'AppUserStatusFilterPlace',
  inheritAttrs: false,
  props: {
    /**
     * プレイスID
     */
    placeId: {
      type: Number,
      default: null,
    },
    /**
     * 対象プレイスとの位置的関係性
     */
    relationship: {
      type: String,
      default: PLACE_RELATIONSHIP.IS,
    },
    /**
     * プレイス距離(m)
     */
    distance: {
      type: Number,
      default: null,
    },
    /**
     * サイトID
     */
    indoorSiteIds: {
      type: Array,
      default: null,
    },
    /**
     * ビルID
     */
    indoorBuildingIds: {
      type: Array,
      default: null,
    },
    /**
     * フロアID
     */
    indoorLevelId: {
      type: Number,
      default: null,
    },
  },
  setup() {
    const { handleApiError } = useHandleApiError()
    return {
      handleApiError,
      PLACE_RELATIONSHIP,
      DISTANCE_OPTIONS,
    }
  },
  data: function () {
    return {
      // ローカル値
      localValue: {
        placeId: null,
        relationship: PLACE_RELATIONSHIP.IS,
        distance: null,
      },
      // プレイス名
      placeName: '',
      // 屋内状態 props の前回値
      // 初回のみ更新評価は要らないので初期値として現在値を設定しておく
      serializedLastIndoorProps: this.$_serializeIndoorProps({
        indoorSiteIds: this.indoorSiteIds,
        indoorBuildingIds: this.indoorBuildingIds,
        indoorLevelId: this.indoorLevelId,
      }),
    }
  },
  computed: {
    /**
     * ローカルのプレイスID選択値に影響のある状態値
     * どちらかが変化した時プレイスID選択値の更新評価を行う
     * @type {Object}
     */
    stateAffectsLocalPlaceId: function () {
      return {
        placeId: this.placeId,
        indoor: this.$_serializeIndoorProps({
          indoorSiteIds: this.indoorSiteIds,
          indoorBuildingIds: this.indoorBuildingIds,
          indoorLevelId: this.indoorLevelId,
        }),
      }
    },
    /**
     * 条件基準部テキスト
     * @type {String}
     */
    criteriaText: function () {
      const { relationship } = this.localValue
      if (relationship === PLACE_RELATIONSHIP.IS) {
        return this.$t('appUserStatusFilterPlace.message.atThisPlace')
      } else if (relationship === PLACE_RELATIONSHIP.CHECKIN) {
        return this.$t('appUserStatusFilterPlace.message.checkingIn')
      } else if (relationship === PLACE_RELATIONSHIP.DISTANCE) {
        const distance = this.DISTANCE_OPTIONS.find(
          (d) => d.value === this.localValue.distance
        )?.text
        return this.$t('appUserStatusFilterPlace.message.within', { distance })
      } else {
        return ''
      }
    },
    /**
     * イベント返却用のテキスト
     * @type {String}
     */
    resultText: function () {
      if (!this.localValue.placeId) return ''
      return this.criteriaText
        ? `${this.placeName}(${this.criteriaText})`
        : this.placeName
    },
  },
  watch: {
    stateAffectsLocalPlaceId: {
      immediate: true,
      handler: function (value) {
        if (this.localValue.placeId !== value.placeId) {
          this.localValue.placeId = value.placeId
        } else if (this.serializedLastIndoorProps !== value.indoor) {
          // プレイスの選択に変更が無く屋内の選択状態が変わったらプレイス選択をクリアする
          this.localValue.placeId = null
        }
        // セレクターにも反映
        // immediate実行時はマウントされていないので存在チェックする
        const selector = this.$refs['place-selector']
        selector && selector.setValue(this.localValue.placeId)
        // 屋内 props の前回値更新
        this.serializedLastIndoorProps = value.indoor
      },
    },
    relationship: {
      immediate: true,
      handler: function (value) {
        this.localValue.relationship = value
      },
    },
    distance: {
      immediate: true,
      handler: function (value) {
        this.localValue.distance = value
      },
    },
    'localValue.placeId': function (value) {
      if (value && !this.localValue.distance) {
        // プレイス選択時に距離の指定が無い場合はデフォルト値を埋める
        this.localValue.distance = this.indoorLevelId ? 5 : 5000
      } else if (!value) {
        // プレイス選択解除された場合は距離と名前をクリア
        this.localValue.distance = null
        this.placeName = ''
      }
    },
    // 値の変更のwatchだが1つの文字列になっているtextを見た方が効率がよい
    resultText: function () {
      this.$_onChange()
    },
  },
  mounted: function () {
    this.$nextTick(() => {
      // defaultPlacesに設定するとイベントが発行されず名前を取得できないので
      // わざとsetValueで値を設定してイベントを発行させる
      this.$refs['place-selector'].setValue(this.localValue.placeId)
      // 初回結果表示のためにpropsの値で変更されたことにする
      this.$_onChange()
    })
  },
  methods: {
    /**
     * 屋内状態の props をシリアライズする
     * @param {Array.<number>} indoorSiteIds サイトID
     * @param {Array.<number>} indoorBuildingIds ビルID
     * @param {number} indoorLevelId フロアID
     * @returns {String} 文字列化した屋内状態
     */
    $_serializeIndoorProps: function ({
      indoorSiteIds,
      indoorBuildingIds,
      indoorLevelId,
    }) {
      return JSON.stringify({
        indoorSiteIds: indoorSiteIds
          ? [...indoorSiteIds].sort((a, b) => a - b)
          : [],
        indoorBuildingIds: indoorBuildingIds
          ? [...indoorBuildingIds].sort((a, b) => a - b)
          : [],
        indoorLevelId,
      })
    },

    /**
     * プレイス選択変更時処理
     * @param {Object} value 変更値
     */
    $_onPlaceSelected: function ([value]) {
      this.localValue.placeId = value?.id || null
      this.placeName = value?.name || ''
    },

    /**
     * 変更時処理
     */
    $_onChange: function () {
      /**
       * 選択値変更イベント
       * @param {Object} value 選択値
       * @param {String} text 選択値表示テキスト
       */
      this.$emit('changed', { value: this.localValue, text: this.resultText })
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../../assets/scss/parts/user/user-status-filter-item';

::v-deep .place-selector {
  width: 100%;
}

.criteria-container {
  width: 100%;
  display: grid;
  grid-template-columns: max-content 1fr;
  align-items: center;
  column-gap: 10px;

  .text {
    font-size: $font-size-small;
    user-select: none;
    white-space: nowrap;
    text-align: left;
    margin: 0;
  }
}

.radio-container {
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 16px;

  .label {
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    margin: 0;
  }

  .distance-selector {
    margin-right: 4px;
  }

  ::v-deep .custom-control {
    // テキストやフォームをラジオボタンに少し近づけるため右側 margin を調整
    margin: 0 -2px 0 0;
  }

  ::v-deep .custom-control-input {
    // 選択時のスタイル => 色を緑系にする
    &:not(:disabled):checked ~ .custom-control-label::before {
      border-color: $entity-color-green;
      background-color: $entity-color-green;
    }
    // focus時のshadowは出さない
    &:focus ~ .custom-control-label::before {
      box-shadow: none;
    }
  }
}
</style>
